import { useId, useRef, type ReactNode } from "react";

export interface TabDef<T extends string> { id: T; label: ReactNode; }

/**
 * APG tabs with automatic activation: arrows, Home and End move and select. Pair with <TabPanel>.
 * idBase lets a TabPanel reference its tab: panel id = `${idBase}-${id}-panel`.
 */
export function Tabs<T extends string>({ tabs, value, onChange, label, idBase, className }: {
  tabs: Array<TabDef<T>>;
  value: T;
  onChange: (t: T) => void;
  label: string;
  idBase?: string;
  className?: string;
}) {
  const auto = useId();
  const base = idBase ?? auto;
  const list = useRef<HTMLDivElement>(null);
  const go = (i: number) => {
    const t = tabs[(i + tabs.length) % tabs.length]!;
    onChange(t.id);
    list.current?.querySelector<HTMLElement>(`[data-tab="${t.id}"]`)?.focus();
  };
  return (
    <div className={`tabs${className ? ` ${className}` : ""}`} role="tablist" aria-label={label} ref={list}>
      {tabs.map((t, i) => (
        <button
          key={t.id}
          id={`${base}-${t.id}`}
          data-tab={t.id}
          role="tab"
          type="button"
          className="tab"
          aria-selected={value === t.id}
          aria-controls={idBase ? `${base}-${t.id}-panel` : undefined}
          tabIndex={value === t.id ? 0 : -1}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") { e.preventDefault(); go(i + 1); }
            else if (e.key === "ArrowLeft") { e.preventDefault(); go(i - 1); }
            else if (e.key === "Home") { e.preventDefault(); go(0); }
            else if (e.key === "End") { e.preventDefault(); go(tabs.length - 1); }
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ idBase, id, active, children, className }: { idBase: string; id: string; active: boolean; children: ReactNode; className?: string }) {
  return (
    <div role="tabpanel" id={`${idBase}-${id}-panel`} aria-labelledby={`${idBase}-${id}`} hidden={!active} className={className} tabIndex={0}>
      {active ? children : null}
    </div>
  );
}

/** Two to four mutually exclusive view options (list/grid, graph/list). Not tabs: no panels. */
export function Segmented<T extends string>({ options, value, onChange, label }: { options: Array<{ id: T; label: ReactNode }>; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}>{o.label}</button>
      ))}
    </div>
  );
}
